import { useToggleHiddenInsightsItem } from 'features/user';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BusinessChart, EyeHidden, Target } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenEditGoalDialog } from 'shared/contexts/goal-form';
import { useOpenGoalProgressHistory } from 'shared/contexts/goal-progress';
import { useUser } from 'shared/hooks/use-user';
import { Goal } from 'shared/types/goal';
import { InsightSections } from 'shared/types/insights';
import { DefaultMetricOptions } from 'shared/types/metric-template';

export type ContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'onOutside'
> & {
  goal: Goal;
  onClose: () => void;
};

export const ContextMenu = forwardRef<HTMLDivElement, ContextMenuProps>(
  ({ goal, onClose, referenceElement, location }, ref) => {
    const { t } = useTranslation();
    const user = useUser();
    const openEditDialog = useOpenEditGoalDialog();
    const openProgressHistoryDialog = useOpenGoalProgressHistory();
    const toggleInsightsHabit = useToggleHiddenInsightsItem(
      InsightSections.Goals,
    );
    const isHidden = !!user?.settings?.insights?.sections?.[
      InsightSections.Goals
    ]?.hiddenItems?.includes(goal.id);

    const canUpdateProgress =
      !!goal.metric?.id &&
      !!goal.metric.entries?.length &&
      ![
        DefaultMetricOptions.CompletedTasks,
        DefaultMetricOptions.SubGoalProgress,
        DefaultMetricOptions.CompletedSubGoals,
      ].includes(goal.metric.id as DefaultMetricOptions);

    const onUpdateProgress = () => {
      openProgressHistoryDialog(goal.id, goal.metric!);
      onClose();
    };

    const onHide = () => {
      toggleInsightsHabit(goal.id);
      onClose();
    };

    const onEdit = () => {
      openEditDialog(goal, goal.id);
      onClose();
    };

    return (
      <PopupMenu
        ref={ref}
        location={location}
        referenceElement={referenceElement}
        position="bottom-start"
        onOutside={onClose}
      >
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton onClick={onEdit} start={<Icon icon={Target} />}>
              {t('context-menus.goal.progress.labels.edit-target')}
            </PopupMenuButton>
          </PopupMenuListItem>
          {canUpdateProgress && (
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onUpdateProgress}
                start={<Icon icon={BusinessChart} />}
              >
                {t('context-menus.goal.progress.labels.show-entries')}
              </PopupMenuButton>
            </PopupMenuListItem>
          )}
        </PopupMenuList>
        <PopupMenuList>
          <PopupMenuListItem>
            <PopupMenuButton onClick={onHide} start={<Icon icon={EyeHidden} />}>
              {t(
                isHidden
                  ? 'context-menus.insights.show.label'
                  : 'context-menus.insights.hide.label',
              )}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      </PopupMenu>
    );
  },
);
